import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard, Search, KanbanSquare, FileText, CreditCard,
    Users, Settings, LogOut, Menu, Plus, ChevronRight, TrendingUp, MessageSquareHeart,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/lib/auth.jsx';
import { CreditCounter } from '@/components/CreditCounter';
import { Badge } from '@/components/ui/badge';
import { Logo } from '@/components/Logo';
import api from '@/lib/api';

const NAV = [
    { to: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard, key: 'dashboard' },
    { to: '/app/leads', label: 'Lead Finder', icon: Search, key: 'leads' },
    { to: '/app/leads/pipeline', label: 'Pipeline', icon: KanbanSquare, key: 'pipeline' },
    { to: '/app/quotes', label: 'Quotes', icon: FileText, key: 'quotes' },
    { to: '/app/market-trends', label: 'Market Trends', icon: TrendingUp, key: 'market-trends' },
    { to: '/app/billing', label: 'Billing', icon: CreditCard, key: 'billing' },
    { to: '/app/team', label: 'Team', icon: Users, key: 'team' },
    { to: '/app/settings', label: 'Settings', icon: Settings, key: 'settings' },
    { to: '/app/feedback', label: 'Feedback', icon: MessageSquareHeart, key: 'feedback' },
];

function SidebarNav({ onItemClick }) {
    const loc = useLocation();
    const path = loc.pathname;
    const isActive = (to) => {
        // Exact match
        if (path === to) return true;
        // Special-case: Lead Finder should NOT match /app/leads/pipeline (Pipeline owns it)
        if (to === '/app/leads') {
            return path.startsWith('/app/leads/') && !path.startsWith('/app/leads/pipeline');
        }
        // Default prefix match
        return path.startsWith(to + '/');
    };
    return (
        <nav className="flex flex-col gap-1 p-3" data-testid="app-sidebar-nav">
            {NAV.map((item) => {
                const Active = isActive(item.to);
                const Icon = item.icon;
                return (
                    <Link
                        key={item.key}
                        to={item.to}
                        onClick={onItemClick}
                        data-testid={`nav-${item.key}`}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium border-l-2 ${
                            Active
                                ? 'bg-amber-50 text-slate-900 border-amber-500'
                                : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                        }`}
                    >
                        <Icon className="h-4 w-4" strokeWidth={1.75} />
                        <span>{item.label}</span>
                    </Link>
                );
            })}
        </nav>
    );
}

export function AppShell({ children }) {
    const { user, org, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [billingMeta, setBillingMeta] = useState(null);

    const subStatus = org?.subscription_status || 'inactive';
    const needsSub = subStatus !== 'active' && subStatus !== 'trialing';

    // Determine if user is on an unlimited plan (Crew/Business) — top-up button is
    // hidden for them. Also surface trial state to show appropriate CTAs.
    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get('/billing/balance');
                if (!cancelled) setBillingMeta(r.data?.credits || {});
            } catch (_e) { /* ignore */ }
        })();
        const handler = () => {
            api.get('/billing/balance').then((r) => setBillingMeta(r.data?.credits || {})).catch(() => {});
        };
        window.addEventListener('qf:credits-refresh', handler);
        return () => { cancelled = true; window.removeEventListener('qf:credits-refresh', handler); };
    }, []);

    const isUnlimited = !!billingMeta?.unlimited;
    const inTrial = !billingMeta?.active && ((billingMeta?.trial?.unlocks || 0) > 0 || (billingMeta?.trial?.quotes || 0) > 0);
    // Show top-up only for credit-gated Solo users with an active sub.
    const showTopupShortcut = !isUnlimited && !inTrial && billingMeta?.active === true;
    // Show "Upgrade" for trial users + inactive users
    const showUpgradeShortcut = !billingMeta?.active;

    useEffect(() => {
        if (needsSub && !window.location.pathname.includes('/billing')) {
            // soft redirect to pricing if no sub
        }
    }, [needsSub]);

    return (
        <div className="min-h-screen flex flex-col bg-background">
            {/* Top bar */}
            <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
                <div className="flex items-center justify-between gap-3 px-4 sm:px-6 h-16">
                    <div className="flex items-center gap-3">
                        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-toggle">
                                    <Menu className="h-5 w-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="left" className="p-0 w-72">
                                <div className="flex items-center px-5 py-4 border-b">
                                    <Logo />
                                </div>
                                <SidebarNav onItemClick={() => setMobileOpen(false)} />
                            </SheetContent>
                        </Sheet>
                        <Link to="/app/dashboard"><Logo /></Link>
                        {subStatus === 'active' ? (
                            <Badge className="hidden md:inline-flex bg-emerald-50 text-emerald-700 border border-emerald-200 font-normal">{org?.tier || ''}</Badge>
                        ) : (
                            <Badge variant="outline" className="hidden md:inline-flex bg-amber-50 text-amber-700 border-amber-200 font-normal">No active plan</Badge>
                        )}
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3">
                        <CreditCounter />
                        {showTopupShortcut && (
                            <Button asChild size="sm" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="topup-shortcut-button">
                                <Link to="/app/billing"><Plus className="h-4 w-4 mr-1" />Top up</Link>
                            </Button>
                        )}
                        {showUpgradeShortcut && (
                            <Button asChild size="sm" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="upgrade-shortcut-button">
                                <Link to="/pricing"><Plus className="h-4 w-4 mr-1" />Upgrade</Link>
                            </Button>
                        )}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" data-testid="user-menu-trigger" className="font-medium">
                                    {(user?.full_name || user?.email || 'U').slice(0, 14)}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel>
                                    <div className="flex flex-col">
                                        <span className="truncate text-sm">{user?.full_name}</span>
                                        <span className="text-xs text-muted-foreground">{user?.email}</span>
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate('/app/settings')} data-testid="user-menu-settings">
                                    <Settings className="h-4 w-4 mr-2" /> Settings
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate('/app/billing')} data-testid="user-menu-billing">
                                    <CreditCard className="h-4 w-4 mr-2" /> Billing
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => { logout(); }} className="text-red-600" data-testid="user-menu-logout">
                                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </header>

            <div className="flex flex-1 w-full">
                {/* Desktop sidebar */}
                <aside className="hidden lg:flex w-[260px] shrink-0 bg-white border-r border-slate-200 flex-col">
                    <SidebarNav />
                    <div className="mt-auto p-3 text-xs text-muted-foreground">
                        <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200">
                            <div className="font-medium text-slate-800">{org?.name || 'Workspace'}</div>
                            <div className="truncate">{org?.tier ? `${org.tier} tier` : 'No active subscription'}</div>
                        </div>
                    </div>
                </aside>

                <main className="flex-1 min-w-0">
                    {needsSub && (
                        <div className="bg-amber-50 border-b border-amber-200 px-4 sm:px-6 py-3">
                            <div className="flex items-center justify-between gap-4">
                                <div className="text-sm text-amber-900">
                                    <b>Subscription required.</b> Pick a plan to start finding leads and sending quotes.
                                </div>
                                <Button asChild size="sm" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="banner-view-pricing">
                                    <Link to="/pricing">View pricing <ChevronRight className="h-3.5 w-3.5 ml-0.5" /></Link>
                                </Button>
                            </div>
                        </div>
                    )}
                    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto w-full">
                        {children}
                    </div>
                </main>
            </div>
        </div>
    );
}
